/**
 * Scroll diagnostics for the intermittent "page stops scrolling / snaps back" bug.
 *
 * Activate by appending `?debug=scroll` to any URL. Inert otherwise.
 *
 * Reports to an on-screen overlay rather than the console, because this bug only
 * reproduces on touch devices where a tethered inspector is not always practical.
 * Everything is also mirrored to console.debug for desktop runs.
 */

const PARAMS = new URLSearchParams(window.location.search);

if (PARAMS.get('debug') === 'scroll') {
  const nativeWindowScrollTo = window.scrollTo.bind(window);
  const nativeElementScrollTo = Element.prototype.scrollTo;
  const nativeScrollIntoView = Element.prototype.scrollIntoView;
  const nativeFocus = HTMLElement.prototype.focus;

  const MAX_ENTRIES = 80;
  const REVERSAL_TRAVEL = 80;
  const REVERSAL_IDLE_MS = 400;
  const MOMENTUM_MS = 1200;
  const OVERLAY_MARKER = 'data-scroll-debug';

  const entries = [];
  let touching = false;
  let lastInputAt = 0;
  let inputCount = 0;
  let recording = false;
  let collapsed = true;

  const overlay = document.createElement('div');
  overlay.setAttribute(OVERLAY_MARKER, '');
  overlay.style.cssText = [
    'position:fixed',
    'inset:auto 0 0 0',
    'z-index:2147483647',
    'max-height:26vh',
    'overflow:auto',
    'margin:0',
    'padding:8px 10px',
    'background:rgba(0,0,0,.88)',
    'color:#0f0',
    'font:11px/1.35 ui-monospace,SFMono-Regular,Menlo,monospace',
    '-webkit-overflow-scrolling:touch',
    'white-space:pre-wrap',
    'word-break:break-word',
  ].join(';');

  const header = document.createElement('div');
  header.style.cssText = 'display:flex;gap:8px;align-items:center;margin-bottom:6px;color:#fff';

  const status = document.createElement('strong');
  status.textContent = 'scroll-debug';

  const copyButton = document.createElement('button');
  copyButton.textContent = 'copy';
  copyButton.style.cssText = 'font:inherit;padding:2px 8px;margin-left:auto';

  const clearButton = document.createElement('button');
  clearButton.textContent = 'clear';
  clearButton.style.cssText = 'font:inherit;padding:2px 8px';

  const list = document.createElement('div');

  header.append(status, copyButton, clearButton);
  overlay.append(header, list);

  /**
   * The log survives a reload.
   *
   * Reloading is how this bug gets recovered from, which means the evidence was being
   * destroyed by the same action used to escape it. Entries persist to localStorage, so
   * the run that ends in a frozen page can be read back from the run after it.
   */
  const STORAGE_KEY = 'scroll-debug:last-session';
  let persistTimer = 0;

  function persist() {
    clearTimeout(persistTimer);
    persistTimer = setTimeout(() => {
      try {
        localStorage.setItem(STORAGE_KEY, entries.join('\n'));
      } catch (_) {
        // Storage full or blocked in private browsing; live logging still works.
      }
    }, 400);
  }

  let previousSession = '';
  try {
    previousSession = localStorage.getItem(STORAGE_KEY) ?? '';
  } catch (_) {
    previousSession = '';
  }

  if (previousSession) {
    const previousButton = document.createElement('button');
    previousButton.textContent = 'copy PREVIOUS';
    previousButton.style.cssText = 'font:inherit;padding:2px 8px;background:#ffd34d';
    previousButton.addEventListener('click', () => {
      navigator.clipboard?.writeText(previousSession).then(
        () => (previousButton.textContent = 'copied prev'),
        () => (previousButton.textContent = 'failed')
      );
    });
    header.appendChild(previousButton);
  }

  const applyCollapsed = () => {
    list.style.display = collapsed ? 'none' : '';
    overlay.style.maxHeight = collapsed ? 'none' : '26vh';
  };

  status.addEventListener('click', () => {
    collapsed = !collapsed;
    applyCollapsed();
  });

  applyCollapsed();

  const mount = () => document.body.appendChild(overlay);
  if (document.body) mount();
  else document.addEventListener('DOMContentLoaded', mount, { once: true });

  copyButton.addEventListener('click', () => {
    navigator.clipboard?.writeText(entries.join('\n')).then(
      () => (copyButton.textContent = 'copied'),
      () => (copyButton.textContent = 'failed')
    );
  });

  clearButton.addEventListener('click', () => {
    entries.length = 0;
    list.textContent = '';
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (_) {
      // Nothing to do; the in-memory log is already cleared.
    }
  });

  /**
   * @param {unknown} node
   * @returns {boolean} Whether the node belongs to this overlay, whose own DOM
   * activity must never be reported (it would recurse through the patches below).
   */
  function isOverlayNode(node) {
    return node instanceof Element && node.closest(`[${OVERLAY_MARKER}]`) !== null;
  }

  /**
   * @param {unknown} node
   * @returns {string} A short, human-identifiable description of the node.
   */
  function describe(node) {
    if (!(node instanceof Element)) return String(node);

    const id = node.id ? `#${node.id}` : '';
    const cls =
      typeof node.className === 'string' && node.className.trim()
        ? `.${node.className.trim().split(/\s+/).slice(0, 2).join('.')}`
        : '';

    let host = node;
    let section = null;
    while (host && !section) {
      section = host.closest?.('[id^="shopify-section-"]') ?? null;
      host = /** @type {any} */ (host.getRootNode())?.host ?? null;
    }

    const custom = node.closest?.('[data-account-drawer], theme-drawer, dialog, swiper-container');

    return [
      `${node.localName}${id}${cls}`,
      section ? `in ${section.id}` : '',
      custom && custom !== node ? `within ${custom.localName}` : '',
    ]
      .filter(Boolean)
      .join(' ');
  }

  /**
   * @returns {string} The nearest stack frame outside this file, across both the
   * V8 (`at fn (url)`) and JavaScriptCore (`fn@url`) stack formats.
   */
  function callsite() {
    const raw = new Error().stack ?? '';
    const frames = raw
      .split('\n')
      .map((line) => line.trim().replace(/^at\s+/, ''))
      .filter((line) => line && line !== 'Error' && !line.includes('scroll-debug.js'));

    return frames[0]?.slice(0, 160) ?? '(native or cross-origin caller)';
  }

  /**
   * @param {string} label
   * @param {string} detail
   * @param {boolean} [warn]
   */
  function record(label, detail, warn = false) {
    if (recording) return;
    recording = true;

    try {
      const stamp = (performance.now() / 1000).toFixed(2).padStart(7, ' ');
      const line = `${stamp}s ${warn ? '!! ' : '   '}[${label}] ${detail}`;

      entries.push(line);
      if (entries.length > MAX_ENTRIES) entries.shift();

      const row = document.createElement('div');
      row.textContent = line;
      if (warn) row.style.color = '#ff5f5f';
      list.appendChild(row);
      while (list.childElementCount > MAX_ENTRIES) list.firstElementChild?.remove();

      overlay.scrollTop = overlay.scrollHeight;
      persist();

      console.debug(`[scroll-debug] ${line}`);
    } finally {
      recording = false;
    }
  }

  /**
   * Input is tracked on three targets because a listener on `document` alone proved
   * unreliable here — bursts of obvious finger-scrolling were arriving with the input
   * counter untouched. `inputCount` is reported on every burst so the log states
   * outright whether input detection is working rather than leaving it to inference.
   */
  for (const type of ['touchstart', 'touchend', 'touchmove', 'wheel', 'keydown', 'pointerdown', 'pointerup']) {
    let counted = null;

    const onInput = (event) => {
      if (event === counted) return;
      counted = event;

      if (type === 'touchstart') touching = true;
      if (type === 'touchend') touching = false;
      lastInputAt = performance.now();
      inputCount += 1;
    };

    for (const target of [window, document, document.documentElement]) {
      target.addEventListener(type, onInput, { passive: true, capture: true });
    }
  }

  window.scrollTo = function (...args) {
    record('window.scrollTo', `${JSON.stringify(args[0] ?? args)} <- ${callsite()}`, true);
    return nativeWindowScrollTo(...args);
  };

  Element.prototype.scrollTo = function (...args) {
    if (!isOverlayNode(this)) {
      record('el.scrollTo', `${describe(this)} ${JSON.stringify(args[0] ?? args)} <- ${callsite()}`, true);
    }
    return nativeElementScrollTo.apply(this, args);
  };

  Element.prototype.scrollIntoView = function (...args) {
    if (!isOverlayNode(this)) {
      record('scrollIntoView', `${describe(this)} <- ${callsite()}`, true);
    }
    return nativeScrollIntoView.apply(this, args);
  };

  HTMLElement.prototype.focus = function (options) {
    if (isOverlayNode(this)) return nativeFocus.call(this, options);

    const before = window.scrollY;
    const result = nativeFocus.call(this, options);
    const delta = window.scrollY - before;

    if (Math.abs(delta) > 1) {
      record('focus caused scroll', `${delta}px by ${describe(this)} <- ${callsite()}`, true);
    } else {
      record('focus', `${describe(this)}${options?.preventScroll ? ' (preventScroll)' : ''}`);
    }

    return result;
  };

  let lastY = window.scrollY;
  let direction = 0;
  let travel = 0;
  let burst = null;
  let burstTimer = 0;

  window.addEventListener(
    'scroll',
    () => {
      const y = window.scrollY;
      const delta = y - lastY;
      lastY = y;

      if (!delta) return;

      const idle = Math.round(performance.now() - lastInputAt);
      const heading = Math.sign(delta);

      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      const rubberBanding = y < 0 || y > maxScroll;

      if (
        direction &&
        heading !== direction &&
        Math.abs(delta) > 4 &&
        Math.abs(travel) > REVERSAL_TRAVEL &&
        idle > REVERSAL_IDLE_MS &&
        !touching &&
        !rubberBanding
      ) {
        record(
          'SCROLL REVERSAL',
          `was ${direction > 0 ? 'down' : 'up'}, now ${heading > 0 ? 'down' : 'up'} ` +
            `${delta > 0 ? '+' : ''}${delta}px -> y=${y}, ${idle}ms since input`,
          true
        );
      }

      if (heading !== direction) {
        direction = heading;
        travel = 0;
      }

      travel += delta;

      if (!touching && idle > MOMENTUM_MS) {
        if (!burst) burst = { from: y - delta, at: performance.now(), idle, deltas: [], inputsAtStart: inputCount };

        burst.deltas.push(delta);
        burst.to = y;

        clearTimeout(burstTimer);
        burstTimer = setTimeout(flushBurst, 250);
      }
    },
    { passive: true }
  );

  /**
   * Emits an unattended scroll as a single line containing its full delta sequence.
   *
   * The shape is the diagnosis: a decelerating run (33,29,24,19,16,13) is an animated
   * scroll, whereas one or two large discrete steps is a layout or anchoring correction.
   * Per-event logging with a size threshold hid this by only ever showing the peak.
   */
  function flushBurst() {
    if (!burst) return;

    const finished = burst;
    burst = null;

    const distance = finished.to - finished.from;
    if (finished.deltas.length < 2 && Math.abs(distance) < 12) return;

    const inputs = inputCount - finished.inputsAtStart;

    record(
      inputs > 0 ? 'user scroll' : 'UNATTENDED SCROLL',
      `y=${finished.from} -> ${finished.to} (${distance > 0 ? '+' : ''}${distance}px) ` +
        `over ${Math.round(performance.now() - finished.at)}ms, ${finished.idle}ms idle at start, ` +
        `${inputs} input events during\n` +
        `             deltas: ${finished.deltas.join(',')}`,
      inputs === 0
    );
  }

  for (const type of ['hashchange', 'popstate']) {
    window.addEventListener(type, () => {
      record(type, `hash="${location.hash}" y=${window.scrollY} <- ${callsite()}`, true);
    });
  }

  let lastHash = location.hash;
  setInterval(() => {
    if (location.hash === lastHash) return;
    record('hash changed', `"${lastHash}" -> "${location.hash}" y=${window.scrollY}`, true);
    lastHash = location.hash;
  }, 200);

  let lastWidth = window.innerWidth;
  let lastHeight = window.innerHeight;

  (window.visualViewport ?? window).addEventListener('resize', () => {
    const width = window.innerWidth;
    const height = window.innerHeight;
    const widthChanged = width !== lastWidth;

    if (!widthChanged && Math.abs(height - lastHeight) < 60) {
      lastHeight = height;
      return;
    }

    record(
      'viewport resize',
      `${lastWidth}x${lastHeight} -> ${width}x${height} y=${window.scrollY}` +
        `${widthChanged ? ' WIDTH CHANGED (zoom?)' : ' (iOS toolbar)'}`,
      widthChanged
    );

    lastWidth = width;
    lastHeight = height;
  });

  /**
   * Reports the full set of properties capable of freezing page scroll, so a stuck
   * page names its own cause instead of requiring another round of guessing.
   *
   * @returns {string} A one-line dump of the current scroll-blocking state.
   */
  function scrollState() {
    const root = document.documentElement;
    const body = document.body;
    const wrapper = document.querySelector('.page-wrapper');

    const rootStyle = getComputedStyle(root);
    const bodyStyle = getComputedStyle(body);
    const wrapperStyle = wrapper ? getComputedStyle(wrapper) : null;

    return [
      `y=${window.scrollY}`,
      `docH=${root.scrollHeight}`,
      `winH=${window.innerHeight}`,
      `scroll-lock=${root.hasAttribute('scroll-lock')}`,
      `html{overflowY:${rootStyle.overflowY} position:${rootStyle.position} touchAction:${rootStyle.touchAction} height:${rootStyle.height}}`,
      `body{overflowY:${bodyStyle.overflowY} position:${bodyStyle.position} top:${bodyStyle.top} height:${bodyStyle.height} touchAction:${bodyStyle.touchAction} overscroll:${bodyStyle.overscrollBehaviorY}}`,
      wrapperStyle
        ? `wrap{overflowY:${wrapperStyle.overflowY} height:${wrapperStyle.height} scrollTop:${wrapper.scrollTop} scrollH:${wrapper.scrollHeight}}`
        : 'wrap=absent',
      `openDialogs=${document.querySelectorAll('dialog[open]').length}`,
    ].join(' ');
  }

  /**
   * @returns {string} `scrollState()`, or the failure reason. A throw here previously
   * would have silently suppressed the line it was being logged into.
   */
  function safeState() {
    try {
      return scrollState();
    } catch (error) {
      return `state unavailable: ${error?.message ?? error}`;
    }
  }

  /**
   * Detects the actual reported symptom: a real finger drag that moves the page by
   * nothing. Scroll-event-based detectors cannot see this, because a frozen page
   * emits no scroll events at all.
   */
  let gestureScrollY = 0;
  let gestureClientY = 0;
  let fingerTravel = 0;
  let gestureTarget = null;

  document.addEventListener(
    'touchstart',
    (event) => {
      const touch = event.touches[0];
      if (!touch || isOverlayNode(event.target)) return;

      gestureScrollY = window.scrollY;
      gestureClientY = touch.clientY;
      fingerTravel = 0;
      gestureTarget = event.target;
    },
    { passive: true, capture: true }
  );

  document.addEventListener(
    'touchmove',
    (event) => {
      const touch = event.touches[0];
      if (!touch || !gestureTarget) return;

      fingerTravel = Math.max(fingerTravel, Math.abs(touch.clientY - gestureClientY));
    },
    { passive: true, capture: true }
  );

  document.addEventListener(
    'touchend',
    () => {
      if (!gestureTarget) return;

      const target = gestureTarget;
      gestureTarget = null;

      if (fingerTravel < 30) return;

      const moved = Math.abs(window.scrollY - gestureScrollY);

      const limit = document.documentElement.scrollHeight - window.innerHeight;
      const atEnd = window.scrollY >= limit - 4 ? ' AT DOCUMENT END' : '';

      if (moved >= 4) {
        record(
          'gesture ok',
          `finger ${Math.round(fingerTravel)}px -> page moved ${moved}px ` +
            `(y ${gestureScrollY} -> ${window.scrollY}, max ${limit})${atEnd} on ${describe(target)}`
        );
        return;
      }

      const scroller = target instanceof Element ? target.closest('[data-open], dialog, .swiper, [style*="overflow"]') : null;

      record(
        'FROZEN: drag moved page 0px',
        `finger travelled ${Math.round(fingerTravel)}px on ${describe(target)}` +
          `${scroller ? ` (inside ${describe(scroller)} — may be an inner scroller)` : ''}\n` +
          `             ${safeState()}`,
        true
      );
    },
    { passive: true, capture: true }
  );

  /**
   * Safari does not implement the `layout-shift` PerformanceObserver entry, so growth
   * above the viewport is detected structurally instead: watch every section's height
   * and the document's total height, and attribute drift to whichever section moved.
   */
  const sectionHeights = new WeakMap();

  const sectionResize = new ResizeObserver((observed) => {
    for (const entry of observed) {
      const element = entry.target;
      const height = entry.contentRect.height;
      const previous = sectionHeights.get(element);

      sectionHeights.set(element, height);

      if (previous == null || Math.abs(height - previous) < 8) continue;

      const above = element.getBoundingClientRect().bottom < 0;

      record(
        'section resize',
        `${element.id || describe(element)} ${Math.round(previous)} -> ${Math.round(height)}px` +
          `${above ? ' ABOVE VIEWPORT' : ''}`,
        above
      );
    }
  });

  const watchSections = () => {
    for (const section of document.querySelectorAll('[id^="shopify-section-"]')) {
      sectionResize.observe(section);
    }
  };

  if (document.body) watchSections();
  else document.addEventListener('DOMContentLoaded', watchSections, { once: true });

  let lastScrollHeight = document.documentElement.scrollHeight;
  setInterval(() => {
    const current = document.documentElement.scrollHeight;
    if (Math.abs(current - lastScrollHeight) < 8) return;

    record('document height', `${lastScrollHeight} -> ${current}px`, true);
    lastScrollHeight = current;
  }, 250);

  if (typeof PerformanceObserver === 'function' && PerformanceObserver.supportedEntryTypes?.includes('layout-shift')) {
    new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.hadRecentInput) continue;

        const sources = (entry.sources ?? [])
          .slice(0, 3)
          .map((source) => describe(source.node))
          .join(', ');

        record('layout shift', `${entry.value.toFixed(4)} ${sources}`, entry.value > 0.01);
      }
    }).observe({ type: 'layout-shift', buffered: true });
  }

  new MutationObserver(() => {
    const locked = document.documentElement.hasAttribute('scroll-lock');
    record('scroll-lock', locked ? `ON <- ${callsite()}` : 'off', locked);
  }).observe(document.documentElement, { attributes: true, attributeFilter: ['scroll-lock'] });

  setInterval(() => {
    const root = document.documentElement;
    const wrapper = document.querySelector('.page-wrapper');
    const rootOverflow = getComputedStyle(root).overflowY;
    const wrapperOverflow = wrapper ? getComputedStyle(wrapper).overflowY : 'n/a';
    const frozen = rootOverflow === 'hidden' && (wrapperOverflow === 'hidden' || wrapperOverflow === 'n/a');

    status.textContent = `scroll-debug y=${Math.round(window.scrollY)} html:${rootOverflow} wrap:${wrapperOverflow}${
      root.hasAttribute('scroll-lock') ? ' LOCKED' : ''
    }`;
    status.style.color = frozen ? '#ff5f5f' : '#fff';
  }, 500);

  record('ready', `${window.innerWidth}x${window.innerHeight} ${navigator.userAgent.slice(0, 90)}`);

  record('state @init', safeState());
  window.addEventListener('load', () => record('state @load', safeState()));
  setTimeout(() => record('state @5s', safeState()), 5000);
}
